import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReusableBreadcrumbs from "../../CommonComponents/ReusableBreadCrumbs";
import { useParams } from "react-router-dom";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import useAuth from "../../../Hooks/useAuth";
import Button from "../../Button";
import IconButton from "../../CommonComponents/IconButton";
import thumbnail from "../../../assets/images/Learningpath_Thumbnail.png";
import ModuleComponent from "./Module";
import Modal from "../../CommonComponents/Modal";
import CreateModule from "./CreateModule";
import { RESULT_STATUS } from "../../../constants";
import CreatePath from "./CreatePath";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Loading from "../../Notification-Loading/Loading/Loading";
import Toast from "../../CommonComponents/Toast";
import { InputDropdownOption } from "../../CommonComponents/InputDropdown";
import Badge from "../../CommonComponents/Badge";
import Tooltip from "../../CommonComponents/Tooltip";

const initialValue = {
  name: "",
  description: "",
  thumbnail: null,
  modules: [],
};

const ITEM_TYPE = "MODULE";

const Module = ({
  item,
  index,
  moveModule,
  onClickModule,
  reorder,
  onDragStart,
  onDelete,
  isSharedLP,
}) => {
  const moduleRef = useRef(null);

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(dragItem) {
      if (!moduleRef.current) return;

      const dragIndex = dragItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      // moveModule(dragIndex, hoverIndex);

      // 🔑 keep index in sync
      dragItem.index = hoverIndex;
    },
    drop(dragItem) {
      if (dragItem.originalIndex !== dragItem.index) {
        // moveModule(dragItem.originalIndex, dragItem.index);
        reorder(dragItem.originalIndex, dragItem.index);
      }
    },
  });

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ITEM_TYPE,
      item: () => {
        onDragStart();
        return { index, originalIndex: index };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index],
  );

  drag(drop(moduleRef));

  return (
    <ModuleComponent
      ref={moduleRef}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      key={item.moduleId}
      item={item}
      onClick={onClickModule}
      onDelete={onDelete}
      isSharedLP={isSharedLP}
    />
  );
};

const Path = () => {
  const { id = null, lpid } = useParams();
  const { setShowNotify, navigate } = useAuth();
  const axios = useAxiosPrivate();
  const baseRoute = id ? `/batches/${id}/courses` : "/learningpath";

  const [pathDetails, setPathDetails] = useState(initialValue);
  const [moduleDetail, setModuleDetail] = useState({
    name: "",
    description: "",
  });
  const [openModal, setOpenModal] = useState(false);
  const [openEditLpModal, setOpenEditLpModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("error");
  const [openMoreList, setOpenMoreList] = useState(false);

  const prevOrderRef = useRef([]);
  const moreOptionRef = useRef(null);

  useEffect(() => {
    if (!openMoreList) return;
    const onClickOutside = () => {
      setOpenMoreList(false);
    };
    document.addEventListener("click", onClickOutside);
    return () => document.removeEventListener("click", onClickOutside);
  }, [openMoreList]);

  useLayoutEffect(() => {
    const fetchPathDetails = async () => {
      try {
        setIsLoading(true);
        const { data } = await axios.get(
          `node/admin/learningpath/${lpid}?moddata=false`,
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          let response =
            typeof data.data === "string" ? JSON.parse(data.data) : data.data;
          let orderedModules = response.modules.sort(
            (a, b) => a.moduleOrderno - b.moduleOrderno,
          );
          response.modules = orderedModules;
          setPathDetails(response || {});
        } else {
          if (data.msg === "The learning path does not exist") {
            setShowNotify({
              title: "Error !",
              msg:
                data.msg ||
                `Unable to get the path list. Please try again later`,
              show: true,
              primaryButton: "Back to Learning Paths",
              primaryButtonFunction: () => {
                setShowNotify((prev) => ({ ...prev, show: false }));
                navigate("/learningpath");
              },
            });
          } else {
            setShowNotify({
              title: "Error !",
              msg:
                data.msg ||
                `Unable to get the path list. Please try again later`,
              show: true,
            });
          }
        }
      } catch (error) {
        console.error("error in fetch path details", error);
        let isUnAuth = error.message.includes("403");
        setShowNotify({
          isUnAuth,
          title: isUnAuth ? "Warning !" : "Error !",
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : `Unable to get the path list. Please try again later`,
          show: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    if (Number.isInteger(+lpid)) fetchPathDetails();
  }, [lpid, axios, setIsLoading, setShowNotify]);

  console.log("path details", pathDetails);

  useEffect(() => {
    const el = document.querySelector(".admin-path-info-container");
    if (el) {
      document.documentElement.style.setProperty(
        "--admin-path-info-height",
        `${el.offsetHeight + 16}px`,
      );
    }
  }, []);

  const items = useMemo(
    () => [
      {
        label: id ? "Curriculum" : "Learningpaths",
        href: id ? null : "/learningpath",
      },
      { label: `${pathDetails.name}` },
    ],
    [id, pathDetails],
  );

  const onSaveModule = (res) => {
    setPathDetails((prev) => ({
      ...prev,
      modules: [
        ...prev.modules,
        {
          moduleId: res.moduleId,
          lpModuleId: res.lpModuleId,
          moduleName: res.name,
          moduleDesc: res.description,
          moduleOrderno: res.lpModuleOrderno ?? 100000,
        },
      ],
    }));
    setModuleDetail({
      name: "",
      description: "",
    });
  };

  const onClickModule = (module) => {
    navigate(`${baseRoute}/${lpid}/module/${module.moduleId}`);
  };

  const moveModule = useCallback((from, to) => {
    if (from === to) return;
    setPathDetails((prev) => {
      const modules = [...prev.modules];

      const [movedItem] = modules.splice(from, 1);
      modules.splice(to, 0, movedItem);

      const updatedModules = modules.map((module, index) => ({
        ...module,
        moduleOrderno: index + 1,
      }));

      return {
        ...prev,
        modules: updatedModules,
      };
    });
  }, []);

  const reorderModules = async (from, to) => {
    try {
      //Parent Re-order
      if (pathDetails.isSharedLP === 1) {
        setToastOpen(true);
        setToastMessage("Reorder is not allowed in shared learning path.");
        setToastType("error");
        return;
      }
      if (from === to) return;

      const copyPathDetails = structuredClone(pathDetails);
      const modules = [...copyPathDetails.modules];

      const presentId = modules[to]?.moduleId;

      const [movedItem] = modules.splice(from, 1);
      const movingId = movedItem.moduleId;
      modules.splice(to, 0, movedItem);

      const topId = modules[to - 1]?.moduleId ?? null;
      const bottomId = modules[to + 1]?.moduleId ?? null;

      const updatedModules = modules.map((module, index) => ({
        ...module,
        moduleOrderno: (index + 1) * 100000,
      }));

      // const payload = updatedModules.map((m) => ({
      //   moduleId: m.moduleId, 
      //   orderno: m.moduleOrderno,
      // }));

      setPathDetails((prev) => ({
        ...prev,
        modules: updatedModules,
      }));

      setIsLoading(true);
      const { data } = await axios.post(
        `node/admin/learningpath/${lpid}/reorder-modules`,
        {
          lpId: lpid,
          scope: "module",
          topId: topId,
          bottomId: bottomId,
          moduleId: movingId,
          targetParentId: presentId,
        },
      );
      if (data.resultCode !== RESULT_STATUS.SUCCESS) {
        throw new Error(
          data.msg ?? `Unable to reorder module. Please try again later`,
        );
      } else {
        setToastMessage("Modules reordered successfully.");
        setToastType("success");
        setToastOpen(true);
      }
    } catch (error) {
      setPathDetails((prev) => ({
        ...prev,
        modules: prevOrderRef.current,
      }));
      let isUnAuth = error.message.includes("403");
      setShowNotify({
        isUnAuth,
        title: isUnAuth ? "Warning !" : "Error !",
        msg: isUnAuth
          ? "You have been logged-out due to inactivity. Login again."
          : `Unable to reorder module. Please try again later`,
        show: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteModule = async (moduleId) => {
    setPathDetails((prev) => ({
      ...prev,
      modules: prev.modules.filter((module) => module.moduleId !== moduleId),
    }));
  };

  const onClickBack = () => {
    navigate("/learningpath");
  };

  /* preview */
  const previewHome = (e) => {
    // e.stopPropagation();
    setOpenMoreList(false);
    window.open(`/preview/home/${lpid}/module?mode=previewlp`, "_blank");
  };

  /* Archive */
  const onLpArchive = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.patch(`node/admin/learningpath/${lpid}`, {
        archived: !pathDetails.archived,
      });
      if (data.resultCode !== RESULT_STATUS.SUCCESS) {
        throw new Error(
          data.msg ??
          `Unable to ${pathDetails.archived ? "unarchived" : "archived"
          } path. Please try again later`,
        );
      } else {
        setToastMessage(
          `Path ${pathDetails.archived ? "unarchived" : "archived"
          } successfully.`,
        );
        setToastType("success");
        setToastOpen(true);
        setPathDetails((path) => ({
          ...path,
          archived: !path.archived,
        }));
        setOpenMoreList(false);
      }
    } catch (error) {
      let isUnAuth = error.message.includes("403");
      setShowNotify({
        isUnAuth,
        title: isUnAuth ? "Warning !" : "Error !",
        msg: isUnAuth
          ? "You have been logged-out due to inactivity. Login again."
          : `Unable to ${pathDetails.archived ? "unarchived" : "archived"
          } path. Please try again later`,
        show: true,
      });
    } finally {
      setIsLoading(false);
    }
  };


  const updateOrderNumberFunction = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`node/admin/learningpath/${lpid}?moddata=true`);
      console.log("response:", response);
      if (response.data.resultCode !== RESULT_STATUS.SUCCESS) {
        throw new Error("Something went wrong");
      }

      const parentModId = [];
      const childParentModId = [];
      const chapterData = [];
      response.data.data.modules.forEach((data) => {
        if (data.moduleId != null) {
          parentModId.push(data.moduleId);
        }

        data.chapters?.forEach((chapter) => {
          if (chapter.chapterId != null) {
            childParentModId.push(chapter.chapterId);
          }

          chapter.chapterData.sort((a,b)=>a.orderno - b.orderno);
          chapter.chapterData?.forEach((content) => {
            if (content.lpcId != null) {
              chapterData.push(content.lpcId);
            }
          });
        });
      });
      console.log("parentMod:", parentModId, "childParet:", childParentModId, "chapter:", chapterData);

      const { data } = await axios.post(
        `node/admin/learningpath/${lpid}/reorder-modules`,
        {
          // modules: payload,
          lpId: lpid,
          scope: "update",
          moduleIds: parentModId,
          childModuleIds: childParentModId,
          chapterData: chapterData
        },
      );
      console.log("data:", data);
      if (data.resultCode !== RESULT_STATUS.SUCCESS) {
        setShowNotify({
          title: "Error !",
          msg: data.msg ?? "Something went wrong",
          show: true,
        });
      }
      else {
        setToastMessage(
          `Reordered successfully.`,
        );
        setToastType("success");
        setToastOpen(true);
      }

    } catch (error) {
      console.log("error;", error);
    }
    finally {
      setIsLoading(false);
    }
  }


  return (
    <div
      className={`admin-path-container${id ? " admin-batch-path-container" : ""
        }`}
    >
      {isLoading && <Loading overlay />}
      <div className="admin-path-info-container">
        <ReusableBreadcrumbs items={items} />
        {!id && (
          <div className={`admin-path-back-btn-wrapper`} onClick={onClickBack}>
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.7998 10.8L0.799805 5.80005L5.7998 0.800049"
                stroke="#667085"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="admin-path-back-btn-text">Back</p>
          </div>
        )}
        <div className="admin-path-info-wrapper">
          <div className="admin-path-info">
            <div className="admin-path-info-title-wrapper">
              <img
                className="admin-path-thumbnail"
                src={pathDetails.thumbnail || thumbnail}
                alt="path-icon"
              />
              <h2 className="admin-path-name">{pathDetails.name}</h2>
              {pathDetails.archived && !id ? (
                <Badge
                  label="Archived"
                  color="grey"
                  icon={{
                    icon: (
                      <svg
                        width="18"
                        height="14"
                        viewBox="0 0 24 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21 6V19H3V6M10 10H14M1 1H23V6H1V1Z"
                          stroke="#101828"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ),
                    position: "start",
                  }}
                />
              ) : null}
            </div>

            <div className="admin-path-info-icons-wrapper">
              <div
                className="admin-path-more-icons-section"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenMoreList((prev) => !prev);
                }}
              >
                <IconButton size="sm" hierarchy="tertiaryGrey">
                  <svg
                    width="4"
                    height="16"
                    viewBox="0 0 4 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="admin-path-more-vertical-icon"
                  >
                    <path
                      d="M2 10C2.55228 10 3 9.55228 3 9C3 8.44772 2.55228 8 2 8C1.44772 8 1 8.44772 1 9C1 9.55228 1.44772 10 2 10Z"
                      stroke="#101828"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 3C2.55228 3 3 2.55228 3 2C3 1.44772 2.55228 1 2 1C1.44772 1 1 1.44772 1 2C1 2.55228 1.44772 3 2 3Z"
                      stroke="#101828"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 17C2.55228 17 3 16.5523 3 16C3 15.4477 2.55228 15 2 15C1.44772 15 1 15.4477 1 16C1 16.5523 1.44772 17 2 17Z"
                      stroke="#101828"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
                {openMoreList && (
                  <div
                    className="admin-path-more-icon-menu input-dropdown-menu"
                    ref={moreOptionRef}
                  >
                    <div className="input-dropdown-options">
                      {pathDetails.isSharedLP === 0 && (
                        <InputDropdownOption
                          value="Edit"
                          label="Edit"
                          onClick={() => {
                            setOpenMoreList(false);
                            setOpenEditLpModal(true);
                          }}
                          className={`admin-path-more-option${pathDetails.archived ? " archived" : ""
                            }`}
                          icon={
                            <svg
                              width="19"
                              height="19"
                              viewBox="0 0 19 19"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.33496 2.60273H4.83496C3.43483 2.60273 2.73476 2.60273 2.19998 2.87521C1.72958 3.1149 1.34713 3.49735 1.10744 3.96775C0.834961 4.50253 0.834961 5.2026 0.834961 6.60273V13.6027C0.834961 15.0029 0.834961 15.7029 1.10744 16.2377C1.34713 16.7081 1.72958 17.0906 2.19998 17.3302C2.73476 17.6027 3.43483 17.6027 4.83496 17.6027H11.835C13.2351 17.6027 13.9352 17.6027 14.4699 17.3302C14.9403 17.0906 15.3228 16.7081 15.5625 16.2377C15.835 15.7029 15.835 15.0029 15.835 13.6027V10.1027M5.83494 12.6027H7.23039C7.63804 12.6027 7.84187 12.6027 8.03368 12.5567C8.20374 12.5159 8.36632 12.4485 8.51544 12.3571C8.68363 12.2541 8.82776 12.1099 9.11601 11.8217L17.085 3.85273C17.7753 3.16237 17.7753 2.04308 17.085 1.35273C16.3946 0.662373 15.2753 0.662372 14.585 1.35273L6.61599 9.32168C6.32774 9.60993 6.18361 9.75406 6.08054 9.92225C5.98916 10.0714 5.92182 10.2339 5.88099 10.404C5.83494 10.5958 5.83494 10.7996 5.83494 11.2073V12.6027Z"
                                stroke="#344054"
                                strokeWidth="1.67"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        />
                      )}

                      {pathDetails.isSharedLP === 0 && !id ? (
                        <InputDropdownOption
                          value={pathDetails.archived ? "Unarchive" : "Archive"}
                          label={pathDetails.archived ? "Unarchive" : "Archive"}
                          onClick={onLpArchive}
                          className={`admin-path-more-option${pathDetails.archived ? " archived" : ""
                            }`}
                          icon={
                            <svg
                              width="20"
                              height="16"
                              viewBox="0 0 24 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21 6V19H3V6M10 10H14M1 1H23V6H1V1Z"
                                stroke="#101828"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        />
                      ) : null}
                      <InputDropdownOption
                        value="Preview"
                        label="Preview"
                        onClick={(event) => previewHome(event)}
                        disable={
                          pathDetails.modules.length <= 0 ||
                          !(
                            pathDetails.modules[0]?.assessmentCount ||
                            pathDetails.modules[0]?.assignmentCount ||
                            pathDetails.modules[0]?.contentCount ||
                            pathDetails.modules[0]?.videoContentCount
                          )
                        }
                        className={`admin-path-more-option${pathDetails.archived ? " archived" : ""
                          }`}
                        icon={
                          <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0.833008 7.50163C0.833008 7.50163 4.16634 0.834961 9.99967 0.834961C15.833 0.834961 19.1663 7.50163 19.1663 7.50163C19.1663 7.50163 15.833 14.1683 9.99967 14.1683C4.16634 14.1683 0.833008 7.50163 0.833008 7.50163Z"
                              stroke="#101828"
                              strokeWidth="1.67"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.99967 10.0016C11.3804 10.0016 12.4997 8.88234 12.4997 7.50163C12.4997 6.12092 11.3804 5.00163 9.99967 5.00163C8.61896 5.00163 7.49967 6.12092 7.49967 7.50163C7.49967 8.88234 8.61896 10.0016 9.99967 10.0016Z"
                              stroke="#101828"
                              strokeWidth="1.67"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="admin-path-description">{pathDetails.description}</p>
        </div>
      </div>
      <div className="admin-path-modules-container">
        <div className="admin-path-modules-title-wrapper">
          <h3 className="admin-path-modules-title">Modules</h3>
          <div className="admin-path-module-button">
            <Tooltip
              title="Update the lp orderno" 
              isIcon={true}
            >
              <Button
                size="sm"
                hierarchy={{ type: "secondaryGrey", buttonText: "Update" }}
                onClick={() => updateOrderNumberFunction()}
                disable={isLoading}
              />
            </Tooltip>
            {pathDetails.isSharedLP === 0 && (
              <Button
                size="sm"
                hierarchy={{ type: "secondaryGrey", buttonText: "Module" }}
                icon={{
                  icon: (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.66829 0.834961V12.5016M0.834961 6.66829H12.5016"
                        stroke="#344054"
                        strokeWidth="1.67"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                  type: "leading",
                }}
                onClick={() => setOpenModal(true)}
              />
            )}
          </div>
        </div>
        <DndProvider backend={HTML5Backend}>
          <div className="admin-path-modules-wrapper">
            {pathDetails.modules?.map((module, index) => (
              <Module
                key={module.moduleId}
                item={module}
                index={index}
                onClickModule={onClickModule}
                moveModule={moveModule}
                reorder={reorderModules}
                onDragStart={() => {
                  prevOrderRef.current = pathDetails.modules;
                }}
                onDelete={onDeleteModule}
                isSharedLP={pathDetails.isSharedLP}
              />
            ))}
          </div>
        </DndProvider>
      </div>

      <Modal
        className="admin-path-module-form-modal"
        title="Module"
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
      >
        <CreateModule
          setOpenModal={setOpenModal}
          lpId={lpid}
          orderno={pathDetails.modules.length + 1}
          setPathDetails={setPathDetails}
          moduleDetail={moduleDetail}
          setModuleDetail={setModuleDetail}
          onSave={onSaveModule}
        />
      </Modal>
      <Modal
        title="Learningpath"
        isOpen={openEditLpModal}
        onClose={() => setOpenEditLpModal(false)}
        className="admin-path-create-modal"
      >
        <CreatePath
          setOpenModal={setOpenEditLpModal}
          pathDetail={pathDetails}
          setPathDetail={setPathDetails}
        />
      </Modal>
      <Toast
        open={toastOpen}
        type={toastType}
        message={toastMessage}
        onClose={() => setToastOpen(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </div>
  );
};

export default Path;
